/**
 * The AI arm of onboarding: «Что вы поставляете?» → «Проверьте, что получилось».
 *
 * It replaces ONE step of the wizard — the category picker — and nothing else.
 * Everything around it (the step counter, the Telegram step, the ИНН step, the
 * gate that decides whether the modal opens at all) is untouched, and
 * `onManual` hands control straight back to the picker at any point. That is
 * the contract with the experiment: the manual branch stays whole, because the
 * AI is an addition to it and the flag can be turned off mid-week.
 *
 * Since 2026-10-20 the modal passes the category tiles in as `picker`, and
 * step 1 is ONE screen: tiles on top, the ИИ form as a highlighted block under
 * them. The proposal and the failure cards still take the whole step.
 *
 * Since 2026-10-22 the ИИ block asks for the DESCRIPTION only. The «Название или
 * БИН/ИИН» field it carried from 2026-10-19 (a company as the second input, grounded
 * on its own past wins) is gone: the company is the wizard's own last step, and the
 * owner met the same question twice in one onboarding («зачем его 2 раза
 * спрашивать»). The API still accepts `tin` / `?company=`; nothing here sends them.
 *
 * The whole state machine lives here rather than in `onboarding-modal.tsx` so
 * the modal keeps its existing shape and its existing tests keep passing with
 * the flag off.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocaleNavigate } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { AI_MAX_TEXT, reportOutcomeQuietly } from '../lib/ai-search-client';
import { useAiPropose } from '../lib/use-ai-propose';
import { countEdits, draftFromProposal, type AiDraft } from '../lib/ai-proposal-draft';
import { saveAiDraft } from '../lib/ai-save-search';
import type { NicheFirstMatch, SavedSearch } from '../lib/saved-search-client';
import { ApiError, NetworkError } from '../lib/api';
import { useAuthOptional } from '../lib/auth-context';
import { savedSearchCapFor } from '../lib/saved-search-cap';
import {
  AiErrorCard,
  AiNotUnderstoodCard,
  AiQuotaLine,
  AiQuotaOutCard,
  AiSparkle,
  aiPrimaryClass,
} from './ai-search-cards';
import { AiProposalReview } from './ai-proposal-review';
import { SavedSearchEditSheet } from './saved-search-edit-sheet';

/**
 * Enough to work with? Three significant words. (A company id or name used to
 * count too, while the block had the «Название или БИН/ИИН» field — see above.)
 *
 * Below that the button is DISABLED rather than red: nothing is wrong yet, the
 * person is still typing. A validation error on a field someone has not
 * finished is how a form teaches people it dislikes them — and a refusal costs
 * nothing here, whereas sending «насос» to the model costs money and comes back
 * as «не понял», which reads like our failure rather than a short sentence.
 */
export function canAsk(text: string): boolean {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.replace(/[^\p{L}\p{N}]/gu, '').length >= 2);
  return words.length >= 3;
}

export function AiOnboardingStep({
  titleId,
  picker,
  footerSlot = null,
  onScreenChange,
  onManual,
  onSaved,
}: {
  titleId: string;
  /**
   * The category tiles, rendered ABOVE the ИИ form on one first screen
   * (kaztender.com, owner 2026-10-20). Measured 15–19.10 over 65 signups: the
   * ИИ-only first screen was filled by 43 % and 46 % did neither it nor the
   * manual pick hidden behind a link, while UzTender's tiles-first step is
   * tapped by 77 %. With it, the ИИ form becomes a highlighted «или опишите
   * своими словами» block under the tiles and the «вручную» link goes — the
   * tiles ARE the manual pick. The picker owns its own saves; this component
   * only lays it out, and hides it once a proposal or a failure card takes the
   * screen. Absent, the step is the ИИ form alone, as before.
   */
  picker?: ReactNode;
  /**
   * A row pinned to the bottom of the sheet (the modal's footer, KZ first
   * screen, owner 2026-10-20): with it, «✦ Подобрать» and «или нажмите на свою
   * сферу выше» render THERE through a portal — same state, same submit — so
   * the button is on screen however far down the fields are. Only on step 1
   * with `picker`; the proposal and the failure cards keep their own buttons,
   * and an empty row is hidden by the modal. `null` (not mounted yet) or
   * absent: the button stays in the flow.
   */
  footerSlot?: HTMLElement | null;
  /**
   * The step swapped one screen for another — the description form, the
   * proposal, a failure card. The modal scrolls its sheet body back to the
   * top here (Redmi 360×691, 2026-10-20: the proposal opened scrolled to where
   * the form had been, title and first codes out of view, ✕ over a code row).
   * Not called on mount.
   */
  onScreenChange?: () => void;
  /** Hand over to the existing category picker (мокап 5: every card offers it). */
  onManual: (reason: 'link' | 'not_understood' | 'quota' | 'error') => void;
  /** A search now exists — the wizard moves on exactly as it does after a
   *  manual pick (invalidate, sample alert, next step). */
  onSaved: (search: SavedSearch, firstMatch: NicheFirstMatch | null) => void;
}) {
  const { t, locale } = useLocale();
  const navigate = useLocaleNavigate();
  // Optional: the step is also rendered (and unit-tested) outside AuthProvider.
  const user = useAuthOptional()?.user ?? null;
  const [text, setText] = useState('');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The row to open the standard builder on, after «Изменить» saved it. */
  const [editTarget, setEditTarget] = useState<SavedSearch | null>(null);
  const [pendingFirstMatch, setPendingFirstMatch] = useState<NicheFirstMatch | null>(null);

  const ai = useAiPropose('onboarding', locale, true);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const aiTitleId = useId();

  function ask() {
    setDraft(null);
    setSaveError(null);
    // The description alone: `tin` / `companyName` left with the company field (2026-10-22).
    ai.ask({ text });
  }

  // Seed the editable draft the first time a proposal lands (and again for
  // each new one). Done on render rather than in an effect so the review never
  // paints one frame with no chips.
  const proposal = ai.phase === 'ready' ? ai.result : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (proposal && seededFor !== proposal.draftId) {
    setSeededFor(proposal.draftId);
    setDraft(draftFromProposal(proposal.proposal));
  }

  /**
   * What became of the draft, kept in a ref so the unmount reporter below can
   * read it without re-subscribing on every keystroke.
   */
  const fate = useRef<{ draftId: string | null; settled: boolean; edits: number }>({
    draftId: null,
    settled: false,
    edits: 0,
  });
  fate.current.draftId = proposal?.draftId ?? fate.current.draftId;
  if (proposal && draft) fate.current.edits = countEdits(proposal.proposal, draft);

  /**
   * A proposal the user walked away from is an OUTCOME, not an absence.
   *
   * Without this the drafts table would show «proposed» forever for everyone
   * who closed the sheet, and «доля принятых без правок» — the number this
   * whole experiment is judged on — would divide by a denominator that quietly
   * mixes "still deciding" with "did not want it".
   */
  useEffect(() => {
    return () => {
      if (!fate.current.draftId || fate.current.settled) return;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'abandoned',
        editsCount: fate.current.edits,
      });
    };
  }, []);

  /** Which screen the step shows — see `onScreenChange`. */
  const screen = proposal
    ? `proposal:${proposal.draftId}`
    : ai.phase === 'failed'
      ? `failed:${ai.failure}`
      : 'ask';
  const shownScreen = useRef(screen);
  const onScreenChangeRef = useRef(onScreenChange);
  onScreenChangeRef.current = onScreenChange;
  // Before paint, so the new screen never shows one frame at the old offset.
  useLayoutEffect(() => {
    if (shownScreen.current === screen) return;
    shownScreen.current = screen;
    onScreenChangeRef.current?.();
  }, [screen]);

  /** Leaving for the manual picker closes the draft out honestly. */
  function leaveToManual(reason: 'link' | 'not_understood' | 'quota' | 'error') {
    if (fate.current.draftId && !fate.current.settled) {
      fate.current.settled = true;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'manual',
        editsCount: fate.current.edits,
      });
    }
    onManual(reason);
  }

  async function save(openBuilder: boolean) {
    if (!proposal || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      // `replace: true` marks the row `fromOnboarding` server-side, as the
      // manual picker's saves are. Unlike the manual niche-switch it does NOT
      // license retiring anything: with `aiDraftId` present the server refuses
      // at the free cap (`saved_search_limit`) instead of switching off the
      // reader's oldest search — see notifications.controller.
      const res = await saveAiDraft(draft, { draftId: proposal.draftId, replace: true });
      // The create endpoint closed the draft out itself (saved / edited_saved),
      // so the unmount reporter must not follow it with «abandoned».
      fate.current.settled = true;
      if (openBuilder) {
        // «Изменить»: the search exists, so the STANDARD builder can open on it
        // — prefilled with exactly this proposal, with every axis the sheet
        // supports. Onboarding advances when the sheet closes, not before.
        setPendingFirstMatch(res.firstMatch);
        setEditTarget(res.search);
      } else {
        onSaved(res.search, res.firstMatch);
      }
    } catch (e: unknown) {
      // A server refusal carries a message worth showing (over the cap,
      // unauthorised); a dropped mobile connection carries «Failed to fetch»,
      // which is English, technical and about nothing the user did.
      // At the free cap the server refuses an ИИ-подбор outright — it never
      // retires a search the reader built by hand (notifications.controller).
      // Say so in the reader's language with the cap the API reported, rather
      // than its English `message`.
      const capFromApi =
        e instanceof ApiError && typeof e.details?.['limit'] === 'number'
          ? (e.details['limit'] as number)
          : null;
      const cap = capFromApi ?? savedSearchCapFor(user);
      setSaveError(
        e instanceof ApiError && e.code === 'saved_search_limit'
          ? t('saved.limitReached', { n: Number.isFinite(cap) ? cap : 1 })
          : e instanceof NetworkError
            ? t('onboarding.niche.saveNetworkError')
            : e instanceof Error && e.message
              ? e.message
              : t('aiOnboarding.saveError'),
      );
    } finally {
      setSaving(false);
    }
  }

  // ── The proposal ────────────────────────────────────────────────────────
  if (proposal && draft) {
    return (
      <>
        <h2
          id={titleId}
          className="mb-2 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
        >
          {t('aiOnboarding.step2.title')}
        </h2>
        <AiProposalReview
          response={proposal}
          draft={draft}
          onDraftChange={setDraft}
          onSave={() => void save(false)}
          onEdit={() => void save(true)}
          saving={saving}
          saveError={saveError}
        />
        {/* The standard builder, opened on the row «Изменить» just created. It
            portals above this modal (both are z-50; the later portal wins), so
            the wizard is still underneath when the sheet closes. */}
        <SavedSearchEditSheet
          search={editTarget}
          open={editTarget !== null}
          // No second «Заполнить с ИИ» here — see the prop's own note.
          aiFill={false}
          onOpenChange={(open) => {
            if (open || !editTarget) return;
            const saved = editTarget;
            setEditTarget(null);
            onSaved(saved, pendingFirstMatch);
          }}
        />
      </>
    );
  }

  // ── The five "no proposal" screens (макет 5) ────────────────────────────
  // `ai_tin_unknown` (a company id with nothing behind it) cannot come back any
  // more — nothing here sends a company — and would land on the generic card.
  if (ai.phase === 'failed') {
    if (ai.failure === 'ai_not_understood') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiNotUnderstoodCard
            // «Уточнить» keeps whatever was typed — re-typing a sentence we
            // just rejected is where people give up.
            onRefine={ai.reset}
            onManual={() => leaveToManual('not_understood')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    if (ai.failure === 'ai_quota_exhausted') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiQuotaOutCard
            quota={ai.quota}
            onManual={() => leaveToManual('quota')}
            onPro={() => navigate('/plans?src=ai_onboarding')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    // timeout · 502 · network · anything unrecognised. None of them spends an
    // attempt, and the copy says so — see `AiErrorCard`.
    return (
      <>
        <h2 id={titleId} className="sr-only">
          {t('aiOnboarding.step1.title')}
        </h2>
        <AiErrorCard onManual={() => leaveToManual('error')} onRetry={ai.retry} />
        <p className="mt-3 text-center text-[12px] text-ink-muted">
          {t('aiOnboarding.manualFooter')}
        </p>
      </>
    );
  }

  // ── Шаг 1 ───────────────────────────────────────────────────────────────
  const busy = ai.phase === 'loading';
  const ready = canAsk(text);
  const combined = picker !== undefined;
  const pinned = combined && footerSlot !== null;

  const submitButton = (
    <button
      type="button"
      onClick={ask}
      disabled={!ready || busy}
      // Disabled must LOOK disabled: on the brand fill `opacity-60` alone still
      // read as a live button (Redmi, 2026-10-20 — tapped, nothing happened).
      // Grey and muted until `canAsk` says there is enough to send.
      className={`${aiPrimaryClass} ${pinned ? 'h-11' : combined ? 'mt-3 h-12' : 'mt-4 h-12'} w-full disabled:border disabled:border-edge disabled:bg-bone disabled:text-ink-muted`}
    >
      {busy ? (
        t('aiOnboarding.step1.loading')
      ) : (
        <>
          <AiSparkle className={`mr-1.5 ${ready ? 'text-white' : 'text-ink-muted'}`} />
          {t('aiOnboarding.step1.submit')}
        </>
      )}
    </button>
  );

  const form = (
    <>
      <textarea
        ref={textRef}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, AI_MAX_TEXT))}
        // Two rows under the tiles: the block has to start on the first screen
        // of a 640 px phone, and a third row adds nothing a sentence needs.
        rows={combined ? 2 : 3}
        maxLength={AI_MAX_TEXT}
        disabled={busy}
        aria-label={combined ? undefined : t('aiOnboarding.step1.title')}
        aria-labelledby={combined ? aiTitleId : undefined}
        placeholder={t('aiOnboarding.step1.placeholder')}
        className={`w-full resize-none rounded-lg border px-3 py-2.5 text-[16px] leading-snug text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60 ${
          combined ? 'border-brand/40 bg-bone-surface' : 'border-edge bg-bone'
        }`}
      />
      <div
        className={`mt-1.5 flex items-start gap-3 ${combined ? 'justify-end' : 'justify-between'}`}
      >
        {/* The example repeats the placeholder; under the tiles the line is
            worth more as space than as a second copy of it. */}
        {!combined && (
          <p className="text-[12px] leading-snug text-ink-muted">
            {t('aiOnboarding.step1.example')}
          </p>
        )}
        {/* BEFORE the button, never after: someone who spends their last
            attempt on half a sentence was never told it was the last one. */}
        <AiQuotaLine quota={ai.quota} variant="counter" className="shrink-0" />
      </div>

      {/* No second input under the description: the company («Название или
          БИН/ИИН», 2026-10-19 → 2026-10-22) is the wizard's own last step. */}

      {!pinned && submitButton}

      {/* Sonnet answers in 6–10 s, so a spinner alone would read as a hang.
          Skeleton rows say "something is being built" and occupy the space the
          chips will take, which stops the sheet jumping when they land. */}
      {busy && (
        <div className="mt-3 flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-7 w-full animate-pulse rounded-full ${combined ? 'bg-bone-surface' : 'bg-bone'}`}
            />
          ))}
        </div>
      )}
    </>
  );

  // ── Шаг 1 на KZ: плитки сверху, ИИ — подсвеченным блоком под ними ───────
  if (combined) {
    return (
      <>
        <h2
          id={titleId}
          className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
        >
          {t('aiOnboarding.tiles.title')}
        </h2>
        <p className="mb-2 text-[13px] leading-[1.5] text-ink-soft">
          {t('aiOnboarding.tiles.desc')}
        </p>

        {picker}

        {/* The second door, on the same screen: highlighted so it reads as an
            offer, not as a form the tiles forgot to hide. */}
        <section
          aria-labelledby={aiTitleId}
          className="mt-2.5 rounded-xl border-[1.5px] border-brand/40 bg-brand-dim p-3"
        >
          <h3
            id={aiTitleId}
            className="flex items-start gap-1.5 text-[14.5px] font-bold leading-snug text-ink"
          >
            <AiSparkle className="mt-px" />
            {t('aiOnboarding.tiles.aiTitle')}
          </h3>
          <p className="mb-2 mt-0.5 text-[12.5px] font-semibold leading-snug text-brand">
            {t('aiOnboarding.tiles.aiCaption')}
          </p>
          {form}
        </section>
        {pinned &&
          footerSlot &&
          createPortal(
            <>
              {submitButton}
              <p className="mt-1 text-center text-[12px] leading-snug text-ink-muted">
                {t('aiOnboarding.tiles.orTap')}
              </p>
            </>,
            footerSlot,
          )}
      </>
    );
  }

  return (
    <>
      <h2
        id={titleId}
        className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
      >
        {t('aiOnboarding.step1.title')}
      </h2>
      <p className="mb-3 text-[13px] leading-[1.55] text-ink-soft">
        {t('aiOnboarding.step1.desc')}
      </p>

      {form}

      <button
        type="button"
        onClick={() => leaveToManual('link')}
        className="mt-3 w-full text-center text-[13px] font-semibold text-ink-soft underline underline-offset-2 transition-colors hover:text-ink"
      >
        {t('aiOnboarding.step1.manual')}
      </button>
    </>
  );
}
